import React, { useContext } from 'react';
import Grid from '@material-ui/core/Grid';
import { Title } from '../../components/title/title';
import { ProtectedPage } from '../../components/protected-page/protected-page';
import Container from '@material-ui/core/Container';
import { useLocation } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import {
  useFetch,
  FilesContext,
  useEventCallback,
  SnackbarContext,
  SnackbarVariant,
  NavigationContext,
} from '@eas/common-web';
import { EvidenceAPI, EvidenceUrl } from '../../enums';
import { UForm } from '../../models';
import CircularProgress from '@material-ui/core/CircularProgress';
import { sendUForm } from './uform-api';

const messages = {
  OK: 'Autorizace proběhla úspěšně',
  EXCEEDED: 'Překročen maximální počet pokusů',
  CANCELED: 'Autorizace byla zrušena',
  FAILED: 'Autorizace selhala',
};

export const useStyles = makeStyles({
  buttonGroup: {
    display: 'flex',
    gap: '20px',
    marginTop: 20,
  },
  button: {
    minWidth: 300,
    whiteSpace: 'nowrap',
  },
  delimeter: {
    marginTop: 20,
  },
  loader: {
    marginTop: 10,
  },
});

export function UFormResult() {
  const { search } = useLocation();
  const { getFileUrl } = useContext(FilesContext);
  const { showSnackbar } = useContext(SnackbarContext);

  const { navigate } = useContext(NavigationContext);

  const classes = useStyles();

  const params = new URLSearchParams(search);

  const result = (params.get('result') || 'FAILED') as keyof typeof messages;
  const uformId = params.get('uformId');

  const [uForm, loading] = useFetch<UForm>(`${EvidenceAPI.UFORM}/${uformId}`, {
    method: 'GET',
    skip: !uformId,
  });

  const handleSendUForm = async (using: 'ESSL' | 'ISDS') => {
    if (!uformId) {
      console.error('UForm id is empty!');
      console.error(uForm);
      return;
    }
    const response = await sendUForm(uformId, using);
    if (response.ok) {
      showSnackbar(
        'Žádost bude odeslána, stav bude aktualizován v průběhu několika minut.',
        SnackbarVariant.SUCCESS
      );
      navigate(EvidenceUrl.UFORM_MINE + `/${uformId}`);
    } else {
      showSnackbar('Žádost se nepodařilo odeslat.', SnackbarVariant.ERROR);
    }
  };

  const message = messages[result] || messages.FAILED;

  const isMZP = uForm?.recipient?.authority?.code === '00164801';

  const handleSendToDatabox = useEventCallback(() => {
    const appTokenPrefix = 'uform-'; // Add prefix for uForm-related appToken
    window.open(
      `https://www.mojedatovaschranka.cz/as/login?atsId=d03ec8392861488b&appToken=${appTokenPrefix}${uformId}`,
      '_blank'
    );
  });

  return (
    <ProtectedPage>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Title name="Výsledek autorizace" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1">
              <ul>
                <li>
                  Výsledek:{' '}
                  <b>
                    {result}, {message}
                  </b>
                </li>
                {!loading && uForm ? (
                  <>
                    <li>
                      Čas úkonu:{' '}
                      <b>
                        {uForm.authorization?.time
                          ? new Date(uForm.authorization?.time).toLocaleString(
                              'cs-CZ'
                            )
                          : ''}
                      </b>
                    </li>
                    <li>
                      ID úkonu: <b>{uForm.authorization?.id}</b>
                    </li>
                  </>
                ) : (
                  <CircularProgress
                    size="18px"
                    disableShrink
                    className={classes.loader}
                  />
                )}
              </ul>
            </Typography>

            {result === 'OK' && (
              <>
                <Typography variant="body1" className={classes.delimeter}>
                  Autorizace proběhla úspěšně. Nyní můžete pokračovat v dalších
                  krocích.
                </Typography>

                <div className={classes.buttonGroup}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    disableElevation
                    onClick={() => handleSendUForm(isMZP ? 'ESSL' : 'ISDS')}
                  >
                    {isMZP ? 'Odeslat na MŽP' : 'Odeslat přes CRŽP'}
                  </Button>

                  {!isMZP && (
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      disableElevation
                      onClick={handleSendToDatabox}
                      style={{
                        // TODO: temporarily hidden - 1.12.2025 Adamuska
                        // https://chat.inqool.cz/default/pl/cu78nqfx5fngpymotc35qo4cpo
                        display: 'none',
                      }}
                    >
                      Odeslat svou DS
                    </Button>
                  )}

                  {uForm?.pdfFile?.file?.id && (
                    <Button
                      variant="contained"
                      color="default"
                      className={classes.button}
                      disableElevation
                      download={true}
                      href={getFileUrl(uForm.pdfFile.file.id)}
                    >
                      Stáhnout PDF
                    </Button>
                  )}
                </div>
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </ProtectedPage>
  );
}
